import { CUSTOM_ELEMENTS_SCHEMA, Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { EasternTimeDatePipe } from '@nationwide/internet-servicing-angular-pipes';
import { ContentfulMapperService, BillingMethodType } from '@nationwide/dgs-angular-billing-common';
import { environment } from '../../../../../../../environments/environment';
import { PaymentFlowType } from '../../../../../../pay-bill/shared/payment-flow-type.service';
import { BasePageComponent } from '../../../../../../shared/base/base-page.component';
import { ISBRouteParams } from '../../../../../../shared/routing/isb-route.model';
import { SessionService } from '../../../../../../shared/session/session.service';
import { UrlUtil } from '../../../../../../shared/url-util/url-util.service';
import { WARNING_MESSAGES } from '../../../../shared/constants/warning-messages';
import { ManagePaymentPreferencesPageTitle } from '../../../shared/models/manage-payment-preferences-page-title.constant';
import { EditAutomaticPaymentFlowService } from '../services/edit-automatic-payments-flow-service';
import { EditAutomaticPaymentConfirmationService } from './edit-automatic-payments-confirmation.service';

import { SafeCurrencyPipe } from '../../../../../../pay-bill/shared/safe-currency-pipe';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { PaymentFormGroup } from '../../../../../../shared/payments/payment-form-group';
import { CtmEventType, CtmProductType } from '../../../../../../shared/service/ctm-service/ctm-model';
import { BillingEventService } from '../../../../../../shared/service/ebi-service/billing-events.service';
import { EBIEventType } from '../../../../../../shared/service/ebi-service/ebi-model';
import { PaymentItem } from '../../../../../payment-preferences/shared/models/payment-item.model';
import { BankAccountPaymentItem } from '../../../../../payment-preferences/shared/models/bankaccount-item.model';
import { BankCardPaymentItem } from '../../../../../payment-preferences/shared/models/bankcard-item.model';
import { HttpCachingService } from '../../../../../../shared/service/http-caching-service/http-caching.service';
import { CelebrusTrackingService } from '../../../../../../shared/service/celebrus-tracking/celebrus-tracking.service';
import { FocusOnInitDirective } from '../../../../../../shared/attr-directives/focus-on-init.directive';
import { PaymentSubHeaderComponent } from '../../../../../../shared/payments/payment-sub-header/payment-sub-header.component';
import { ButtonWrapperComponent } from '@nationwide/internet-servicing-angular-components';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-edit-automatic-payments-confirmation',
    templateUrl: './edit-automatic-payments-confirmation.component.html',
    styleUrls: ['./edit-automatic-payments-confirmation.component.scss'],
    imports: [FocusOnInitDirective, PaymentSubHeaderComponent, ButtonWrapperComponent, DatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EditAutomaticPaymentsConfirmationComponent extends BasePageComponent implements OnInit, OnDestroy {
    _successMessage: string;
    amountDue: number;
    amountDueDate: string;
    pageName = ManagePaymentPreferencesPageTitle.automaticPayments.edit.confirmation;
    formattedSubmittedDate: string;
    formattedEffectiveDate: string;
    editErrorMessage = signal('');
    defaultEffectiveDateText = 'next bill due date';

    constructor(
        private paymentFlowType: PaymentFlowType,
        private easternTimeDatePipe: EasternTimeDatePipe,
        private safeCurrencyPipe: SafeCurrencyPipe,
        router: Router,
        session: SessionService,
        private editPaymentFlow: EditAutomaticPaymentFlowService,
        private editAutomaticPaymentConfirmationService: EditAutomaticPaymentConfirmationService,
        private urlUtil: UrlUtil,
        private readonly celebrusTrackingService: CelebrusTrackingService,
        private eventService: BillingEventService,
        private httpCachingService: HttpCachingService,
        @Inject('window') private window: Window,
        @Inject('logger') private logger: LoggerService,
        private contentfulMapperService: ContentfulMapperService
    ) {
        super(session, router);
    }

    ngOnInit(): void {
        this.session.currentPage = this.pageName;
        this.setFormattedDates();
        this.setVariablesFromApiResponse();
        this.sendOnPageLoadEbiEvent();
        this.sendEditAutoPaySuccessCtmEvent();
        this.logPreviouslyHadSAP417ErrorOnSuccess();
        this.initializeErrorMessage();
        this.httpCachingService.clearCache();
    }

    ngOnDestroy(): void {
        this.editPaymentFlow.clear();
        this.celebrusTrackingService.clearCelebrusSessionStorage();
    }

    setVariablesFromApiResponse(): void {
        this.amountDue = this.safeCurrencyPipe.transformToCurrency(this.editAutomaticPaymentConfirmationService.amountDue);
        this.amountDueDate = this.editAutomaticPaymentConfirmationService.amountDueDate ?
            this.easternTimeDatePipe.transform(this.editAutomaticPaymentConfirmationService.amountDueDate) : '';
    }

    get urlParams(): ISBRouteParams {
        return {
            accountNumber: this.session.billingAccountNumber,
            sessionId: this.session.a2aSessionId,
            policyNumber: this.paymentFlowType.pKey,
            referringPage: 'review'
        };
    }

    sendEditAutoPaySuccessCtmEvent(): void {
        if (this.apiCallSuccessful) {
            this.eventService.handleCtmEvent(
                CtmEventType.PP_EDIT_EASY_PAYMENT_METHOD, {
                productType: CtmProductType.BILLING,
                paymentMethod: this.editAutomaticPaymentConfirmationService.fetchCtmPaymentMethod(this.paymentMethod, this.editAutoPaymentForm)
            });
        }
    }

    onContinue(): void {
        if (this.amountDueExists()) {
            this.navigateToMAP();
        } else {
            this.navigateToPaymentPreferences();
        }
    }

    secondayNavigation(metaData: ButtonMetaData): void {
        if (metaData.label === 'Account Summary') {
            this.navigateToCAM();
        } else {
            this.router.navigateByUrl(metaData.navigation);
        }
    }

    amountDueExists(): boolean {
        const amountDueExists = this.editAutomaticPaymentConfirmationService.amountDue > 0;
        if (this.session.isSAP) {
            return amountDueExists && this.automaticPaymentSuspended;
        } else if (this.session.isNBP) {
            return amountDueExists && this.editPaymentFlow.canPayNow;
        }
    }

    navigateToCAM(): void {
        this.window.location.href = environment.CAM_HOME;
    }

    navigateToPaymentPreferences(): void {
        this.router.navigateByUrl(environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.paymentPreferences(this.urlUtil.hashParamsString));
    }

    navigateToMAP(): void {
        this.router.navigateByUrl(environment.ISB.ENDPOINTS.RELATIVE.payBillLanding(this.urlParams));
    }

    showPrintDialog(): void {
        this.sendOnClickOfPrintButtonEbiEvent();
        this.window.print();
    }

    sendOnPageLoadEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_LOAD_OF_EDIT_AUTOMATIC_PAYMENTS_CONFIRMATION_PAGE, {
            paymentPlan: this.editPaymentFlow.paymentPlan
        });
    }

    sendOnClickOfPrintButtonEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_CLICK_OF_PRINT_BTN_EDIT_AUTOMATIC_PAYMENTS_CONFIRMATION_PAGE, {
            paymentPlan: this.editPaymentFlow.paymentPlan
        });
    }

    setFormattedDates(): void {
        const dateFormat = 'MM/dd/yyyy';
        const timeFormat = 'hh:mm aa';
        const currentDateTime = new Date();

        const submittedDate = this.easternTimeDatePipe.transform(currentDateTime, dateFormat);
        const submittedTime = this.easternTimeDatePipe.transform(currentDateTime, timeFormat);
        this.formattedSubmittedDate = `${submittedDate}, ${submittedTime} ET`;

        const effectiveDate = this.effectiveBillDueDate;
        if (effectiveDate && this.isEffectiveDateValid(submittedDate, effectiveDate)) {
            const effDate = this.easternTimeDatePipe.transform(effectiveDate, dateFormat);
            const effTime = this.easternTimeDatePipe.transform(effectiveDate, timeFormat);
            this.formattedEffectiveDate = `${effDate}, ${effTime} ET`;
        } else {
            this.formattedEffectiveDate = this.defaultEffectiveDateText;
        }
    }

    private logPreviouslyHadSAP417ErrorOnSuccess(): void {
        if (this.apiCallSuccessful && this.session.hadAutoPay417Error === 'true') {
            this.logger.info('User successfully edited automatic payments after receiving SAP 417 error');
        }
    }

    get isSwitchingPaymentMethodTypes(): boolean {
        const currentType = this.editPaymentFlow.currentBillingMethodType;
        const newType: BillingMethodType = this.paymentMethod?.isBankCard ? 'Recurring Bankcard' : 'Recurring EFT';
        return currentType !== newType;
    }

    get isInClosedWindow(): boolean {
        return !this.editPaymentFlow.canCancelOrSuspendAutomaticPayments;
    }

    get hasCurrentBillBalance(): boolean {
        return this.editAutomaticPaymentConfirmationService.amountDue > 0;
    }

    get isEffectiveNextBill(): boolean {
        return this.isSwitchingPaymentMethodTypes
            || this.isInClosedWindow
            || this.editPaymentFlow.automaticPaymentSuspended
            || !this.hasCurrentBillBalance;
    }

    get shouldShowOutstandingPayment(): boolean {
        return this.session.isNBP
            && this.hasCurrentBillBalance
            && (this.isSwitchingPaymentMethodTypes || this.isInClosedWindow);
    }

    get effectiveBillDueDate(): Date {
        return this.isEffectiveNextBill
            ? this.editPaymentFlow.nextBillDueDate
            : this.editPaymentFlow.currentBillDueDate;
    }

    get oldPaymentMethodDisplayString(): string {
        const originalMethod = this.editPaymentFlow.originalPaymentMethod;
        if (!originalMethod) {
            return '';
        }

        const lastFourDigits = 4;
        const lastThreeDigits = 3;

        if (originalMethod.isBankCard) {
            const cardItem = <BankCardPaymentItem>originalMethod.item;
            const cardType = cardItem.cardType?.toUpperCase() || 'CARD';
            const last4 = cardItem.maskedCardNumber?.slice(-lastFourDigits) || '****';
            return `${cardType} ${last4}`;
        } else if (originalMethod.isBankAccount) {
            const accountItem = <BankAccountPaymentItem>originalMethod.item;
            const accountType = accountItem.accountType || 'Account';
            const last3 = accountItem.maskedBankAccountNumber?.slice(-lastThreeDigits) || '***';
            return `${accountType} ****${last3}`;
        }

        return '';
    }

    get secondaryButton(): ButtonMetaData {
        const buttonMetaData: ButtonMetaData = {
            label: 'Payment preferences',
            navigation: environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.paymentPreferences(this.urlUtil.hashParamsString)
        };
        return buttonMetaData;
    }

    get editAutoPaymentForm(): PaymentFormGroup {
        return this.editPaymentFlow.editAutomaticPaymentForm;
    }

    get paymentMethod(): PaymentItem {
        return this.editPaymentFlow.selectedPayMethod;
    }

    get paymentType(): string {
        return this.editAutomaticPaymentConfirmationService.paymentType;
    }

    get maskedNumber(): string {
        return this.editAutomaticPaymentConfirmationService.maskedNumber;
    }

    get upNextLabel(): string {
        return this.editAutomaticPaymentConfirmationService.upNextLabel;
    }

    get apiCallSuccessful(): boolean {
        return this.editPaymentFlow.successfullyUpdatedEnrollment;
    }

    initializeErrorMessage(): void {
        this.editErrorMessage.set(WARNING_MESSAGES.API_FAILURE);
        const bankCardErrorCode = 417;
        if (!this.editPaymentFlow.wasRecurringAuthorizationFormSuccessfullySaved) {
            this.contentfulMapperService.getContentfulBillingContent(environment.PRODUCTION).subscribe((contentfulBillingContent) => {
                this.editErrorMessage.set(contentfulBillingContent?.NuxeoUpdtAutopaySaveFailureMessage?.content || this.editErrorMessage());
            });
        } else if (this.session.isSAP && this.editPaymentFlow?.errorDetails?.errorCode === bankCardErrorCode) {
            this.editErrorMessage.set(this.editPaymentFlow.errorDetails.errorMessage || WARNING_MESSAGES.GENERIC_417_ERROR);
            this.logger.info(`User received SAP 417 error on auto pay edit submission: ${this.editErrorMessage()}`);
            this.session.hadAutoPay417Error = 'true';
        }
    }

    get automaticPaymentSuspended(): boolean {
        return this.editPaymentFlow.automaticPaymentSuspended;
    }

    get canPayNow(): boolean {
        return this.editPaymentFlow.canPayNow;
    }

    private isEffectiveDateValid(
        submittedDate: Date | string,
        effectiveDate: Date | string
    ): boolean {
        const submitted = new Date(submittedDate);
        const effective = new Date(effectiveDate);

        return effective >= submitted;
    }
}
export interface ButtonMetaData {
    label: string;
    navigation: string;
}
